import React, { useMemo } from "react";

import * as SC from "./PostEditorYoutube.styled";
import * as VerticalSC from "./../post-editor-vertical/PostEditorVertical.styled";

import logger from "../../../../../utils/LoggerFrontend";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import ChannelDataEditor from "../../Campaign__Create_ChannelDataEditor.jsx";
import { ForwardIcon } from "../../Svg";
import { channelsByShortName } from "../../../../../data/app__channels";
import CampaignCreatePerformanceEditor from "../../campaign-create-performance-editor/CampaignCreatePerformanceEditor.jsx";
import { Tooltip } from "../../Components";
import { useTranslation } from "react-i18next";
import TemplatePostPreview from "../../TemplatePost/TemplatePost__Preview/TemplatePostPreview.jsx";
import useMediaAssets from "../hooks/useMediaAssets";

import TemplatePostStore from "../../TemplatePost/TemplatePostStore";
import { withRefluxStore } from "../../../../../Stores";
import { VERTICAL_TEMPLATE_IDS } from "../../constants.js";

/**
 * GreyBar component renders a specified number of grey bars.
 *
 * @param {Object} props - The component props.
 * @param {number} [props.number=0] - The number of grey bars to render. Defaults to 0.
 * @returns {JSX.Element} The rendered component.
 */
export const GreyBar = ({ number = 0 }) => {
  const bars = Array.from({ length: number });

  return (
    <div>
      {bars.map((_, index) => (
        <SC.GreyBar key={index} />
      ))}
    </div>
  );
};

const PostEditorYoutube = ({
  orgaId,
  campaignId,
  postData,
  channelData,
  updatePost,
  updateChannelData,
  mobile,
  editable,
  logoUrl,
  channel,
  selectedCreativeIndexToEdit,
  setSelectedCreativeIndexToEdit,
  campaignLanguage,
  // Store
  template,
  currentInputProps,
  inputProps
}) => {
  const { t } = useTranslation();

  const mediaAssets = useMediaAssets(postData);

  const selectedCreative = useMemo(
    () => postData.creatives?.[selectedCreativeIndexToEdit],
    [postData.creatives, selectedCreativeIndexToEdit]
  );

  logger.log("PostEditorYoutube:render", "render:", {
    postData,
    channelData,
    mobile,
    editable,
    channel,
    mediaAssets,
    selectedCreative,
    selectedCreativeIndexToEdit,
    campaignId,
    template,
    currentInputProps,
    inputProps
  });

  const multiTemplatesOrientation =
    template?.orientation === "multi" && inputProps?.orientation === "vertical";

  const verticalTemplate =
    VERTICAL_TEMPLATE_IDS.includes(template?.id) || multiTemplatesOrientation;

  const templateSC = verticalTemplate ? VerticalSC.VERTICAL_IMAGE_SPEC : SC.YOUTUBE_IMAGE_SIZE_SPEC;

  const minAspect = verticalTemplate ? templateSC.minAspect : channelsByShortName.youtube.minAspect;
  const maxAspect = verticalTemplate ? templateSC.maxAspect : channelsByShortName.youtube.maxAspect;

  const templatePreviewSizes = {
    minHeight: templateSC.minHeight,
    maxHeight: templateSC.maxHeight,
    width: templateSC.pictureWidth,
    minAspect: minAspect,
    maxAspect: maxAspect
  };

  return (
    <SC.PostEditorWrapper verticalTemplate={verticalTemplate}>
      <SC.HeaderWrapper>
        <SC.Logo src={logoUrl} alt="" />
      </SC.HeaderWrapper>
      <SC.BodyWrapper>
        {!verticalTemplate && <GreyBar number={3} />}
        <SC.Editor verticalTemplate={verticalTemplate}>
          <TemplatePostPreview
            orgaId={orgaId}
            creatives={postData.creatives}
            creativeAssets={postData.creativeAssets}
            selectedMediaAssetIndexToEdit={selectedCreativeIndexToEdit}
            mediaAssets={mediaAssets}
            updatePost={updatePost}
            channel={channel}
            editable={editable}
            templatePreviewSizes={templatePreviewSizes}
          />
          <SC.TitleWrapper>
            <ChannelDataEditor
              updateChannelData={updateChannelData}
              value={channelData.title}
              dataKey="title"
              textStyle={SC.titleStyle}
              limit={channelsByShortName[channel].limits.title}
              label={t("Job Title")}
              tooltip={t("Job Title")}
              editable={editable}
              dark
              bannedCharaters={["!", "<", ">"]}
            />
          </SC.TitleWrapper>
          <SC.DescriptionWrapper>
            <SC.EditorWrapper>
              <CampaignCreateTeaserEditor
                channel={channel}
                updatePost={updatePost}
                teaserText={postData.teaserText}
                editable={editable}
                teaserTextStyle={SC.teaserTextStyle}
                limit={channelsByShortName[channel].limits.teaserText}
                minRows={2}
                smallButtons
                disableEmojis={true}
                bannedCharaters={["!", "<", ">"]}
                isAutoGenerated={postData?.isAutoGenerated}
                autoGeneratedText={postData?.autoGeneratedText}
                isEdited={postData?.editedStates?.teaserText}
                rejectUppercaseWords={true}
                uppercaseWhitelist={["PKW", "LKW", "IMS", "SAP"]}
              />
            </SC.EditorWrapper>
            <SC.EditorFooter>
              <SC.CompanyNameWrapper>
                <ChannelDataEditor
                  updateChannelData={updateChannelData}
                  value={channelData.companyName}
                  dataKey="companyName"
                  textStyle={SC.companyNameStyle}
                  limit={channelsByShortName[channel].limits.companyName}
                  label={t("Company Name")}
                  tooltip={t("Company Name")}
                  editable={editable}
                />
                <SC.Sponsored data-tip="" data-for="youtube-sponsored-tooltip">
                  {t("Sponsored", { lng: campaignLanguage })}
                </SC.Sponsored>
                <Tooltip
                  id="youtube-sponsored-tooltip"
                  effect="solid"
                  place="top"
                  backgroundColor="#1C1928"
                  offset={{ top: -5 }}
                >
                  {t("Playout as sponsored advertisement")}
                </Tooltip>
              </SC.CompanyNameWrapper>
              <SC.ArrowButton target="_blank" href={channelData.url}>
                <ForwardIcon style={{ width: "100%" }} />
              </SC.ArrowButton>
            </SC.EditorFooter>
            <CampaignCreatePerformanceEditor
              creatives={postData.creatives}
              creativeAssets={postData.creativeAssets}
              mediaAssets={mediaAssets}
              channel={channel}
              editable={editable}
              updatePost={updatePost}
              padding="8px 0 0 0"
              selectedCreativeIndexToEdit={selectedCreativeIndexToEdit}
              setSelectedCreativeIndexToEdit={setSelectedCreativeIndexToEdit}
            />
          </SC.DescriptionWrapper>
        </SC.Editor>
        {!verticalTemplate && <GreyBar number={2} />}
      </SC.BodyWrapper>
    </SC.PostEditorWrapper>
  );
};

export default withRefluxStore(
  PostEditorYoutube,
  [TemplatePostStore],
  ["template", "currentInputProps", "inputProps"]
);
